import React, { useCallback, useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Play, RotateCcw, Repeat, Monitor, Smartphone, MonitorSmartphone, Eye, EyeOff } from 'lucide-react'

/**
 * Break Room Cup — promotional video stage (SEQ 1: Hook / Brand, 0:00–0:08).
 *
 * Renders the REAL landing hero inside a fixed cinematic stage (via an iframe to
 * `/?guest=1`, so the design + entrance animations are pixel-exact), then
 * overlays the scripted beats:
 *   - slow Ken Burns zoom on the hero + World Cup 2026 badge
 *   - burned-in caption: "Break Room Cup · Predict · Race · Chess · Compete"
 *
 * Three render modes:
 *   - `web`        : landscape 16:9, full-bleed hero
 *   - `web-mobile` : landscape 16:9, desktop browser frame + phone mockup
 *   - `mobile`     : portrait 9:16, single centered phone (Reels/Stories format)
 *
 * Controls live OUTSIDE the recordable stage and can be hidden for a clean
 * screen capture (toggle with the eye button or the Esc key).
 */

const SEQ1_MS = 8000
const LOOP_GAP_MS = 900

const LANDSCAPE = { w: 1280, h: 720 }
const PORTRAIT = { w: 720, h: 1280 }

const HERO_SRC = `${process.env.PUBLIC_URL || ''}/?guest=1`

const CAPTION_PARTS = ['Break Room Cup', 'Predict', 'Race', 'Chess', 'Compete']

type Mode = 'web' | 'web-mobile' | 'mobile'

/** Scale the fixed-size stage to fit the viewport (recompute on resize). */
function useFitScale(w: number, h: number, pad = 48) {
  const [scale, setScale] = useState(1)
  useEffect(() => {
    const calc = () => {
      const sw = (window.innerWidth - pad) / w
      const sh = (window.innerHeight - pad) / h
      setScale(Math.min(sw, sh))
    }
    calc()
    window.addEventListener('resize', calc)
    return () => window.removeEventListener('resize', calc)
  }, [w, h, pad])
  return scale
}

/**
 * Renders an iframe at a logical viewport size, then scales it to fill a target
 * box (cropping the bottom — we keep the top of the page in view).
 */
const ScaledFrame: React.FC<{
  src: string
  logicalW: number
  logicalH: number
  targetW: number
  targetH: number
  cycle: number
  radius?: number
}> = ({ src, logicalW, logicalH, targetW, targetH, cycle, radius = 0 }) => {
  const scale = targetW / logicalW
  return (
    <div
      style={{ width: targetW, height: targetH, borderRadius: radius }}
      className="relative overflow-hidden bg-[#0b1220]"
    >
      <iframe
        key={cycle}
        title="brc-hero"
        src={src}
        scrolling="no"
        style={{
          width: logicalW,
          height: logicalH,
          border: 0,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          pointerEvents: 'none',
        }}
      />
    </div>
  )
}

const CaptionBar: React.FC<{ compact?: boolean }> = ({ compact = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 60 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 3.0, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
    className={`absolute left-1/2 -translate-x-1/2 z-30 ${compact ? 'bottom-16 w-[90%]' : 'bottom-10'}`}
  >
    <div
      className={`relative inline-flex items-center justify-center gap-2.5 rounded-2xl overflow-hidden border border-white/15 shadow-2xl ${
        compact ? 'flex-wrap px-4 py-3' : 'px-6 py-3.5'
      }`}
      style={{
        background: 'linear-gradient(135deg, rgba(15,23,42,0.78) 0%, rgba(30,27,75,0.74) 100%)',
        backdropFilter: 'blur(10px)',
      }}
    >
      {/* shimmer sweep */}
      <motion.span
        aria-hidden
        className="absolute top-0 h-full w-[35%] bg-white/25 blur-sm -skew-x-12"
        initial={{ x: '-140%' }}
        animate={{ x: ['-140%', '320%'] }}
        transition={{ delay: 3.6, duration: 1.6, ease: 'easeInOut', repeat: Infinity, repeatDelay: 1.4 }}
      />
      {CAPTION_PARTS.map((part, i) => (
        <React.Fragment key={part}>
          {i > 0 && <span className="text-white/35 text-lg leading-none">·</span>}
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 3.2 + i * 0.12, duration: 0.4 }}
            className={
              i === 0
                ? `font-display font-bold text-white tracking-tight whitespace-nowrap ${compact ? 'text-lg' : 'text-xl sm:text-2xl'}`
                : `font-semibold text-white/85 whitespace-nowrap ${compact ? 'text-sm' : 'text-base sm:text-lg'}`
            }
          >
            {part}
          </motion.span>
        </React.Fragment>
      ))}
    </div>
  </motion.div>
)

/** Mock browser chrome wrapper for the desktop frame in web+mobile mode. */
const BrowserChrome: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="rounded-2xl overflow-hidden border border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] bg-[#0b1220]">
    <div className="flex items-center gap-2 h-9 px-3 bg-[#11192b] border-b border-white/10">
      <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
      <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
      <span className="w-3 h-3 rounded-full bg-[#28c840]" />
      <div className="mx-auto flex items-center gap-2 px-4 h-6 rounded-full bg-black/40 border border-white/10">
        <span className="text-[11px] text-white/60 font-medium">breakroomcup.com</span>
      </div>
      <span className="w-3 h-3" />
    </div>
    {children}
  </div>
)

/** Reusable phone mockup wrapping a scaled mobile-layout iframe. */
const PhoneMock: React.FC<{
  cycle: number
  outerWidth: number
  screenW: number
  screenH: number
}> = ({ cycle, outerWidth, screenW, screenH }) => (
  <div
    className="relative rounded-[2.6rem] bg-[#0b0f1a] p-2.5 border border-white/12 shadow-[0_40px_90px_-25px_rgba(0,0,0,0.8)]"
    style={{ width: outerWidth }}
  >
    <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-28 h-5 rounded-b-2xl bg-[#0b0f1a] z-10" />
    <div className="rounded-[2.1rem] overflow-hidden">
      <ScaledFrame src={HERO_SRC} logicalW={390} logicalH={844} targetW={screenW} targetH={screenH} cycle={cycle} />
    </div>
  </div>
)

const Promo: React.FC = () => {
  const [mode, setMode] = useState<Mode>('web')
  const stage = mode === 'mobile' ? PORTRAIT : LANDSCAPE
  const scale = useFitScale(stage.w, stage.h)
  const [cycle, setCycle] = useState(0)
  const [loop, setLoop] = useState(true)
  const [showUI, setShowUI] = useState(true)
  const loopTimer = useRef<number | null>(null)

  const replay = useCallback(() => setCycle((c) => c + 1), [])

  // Auto-loop for easy review.
  useEffect(() => {
    if (loopTimer.current) window.clearTimeout(loopTimer.current)
    if (!loop) return
    loopTimer.current = window.setTimeout(() => {
      setCycle((c) => c + 1)
    }, SEQ1_MS + LOOP_GAP_MS)
    return () => {
      if (loopTimer.current) window.clearTimeout(loopTimer.current)
    }
  }, [cycle, loop, mode])

  // Esc toggles the control UI for clean recording; R replays.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowUI((s) => !s)
      if (e.key.toLowerCase() === 'r') replay()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [replay])

  return (
    <div className="fixed inset-0 z-[120] bg-black flex items-center justify-center overflow-hidden">
      {/* ───────────── Recordable stage (scaled to fit) ───────────── */}
      <div
        style={{ width: stage.w, height: stage.h, transform: `scale(${scale})` }}
        className="relative shrink-0 overflow-hidden bg-[#070b14]"
      >
        {/* Cinematic backdrop */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0b1220] via-[#0a0f1d] to-[#120a24]" />

        <AnimatePresence mode="wait">
          <motion.div
            key={`${mode}-${cycle}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="absolute inset-0"
          >
            {/* ── WEB: full-bleed hero with slow Ken Burns zoom ── */}
            {mode === 'web' && (
              <motion.div
                className="absolute inset-0 origin-center"
                initial={{ scale: 1.02 }}
                animate={{ scale: 1.12 }}
                transition={{ duration: SEQ1_MS / 1000, ease: 'easeInOut' }}
              >
                <ScaledFrame
                  src={HERO_SRC}
                  logicalW={1280}
                  logicalH={720}
                  targetW={LANDSCAPE.w}
                  targetH={LANDSCAPE.h}
                  cycle={cycle}
                />
              </motion.div>
            )}

            {/* ── WEB + MOBILE: desktop browser frame + phone mockup ── */}
            {mode === 'web-mobile' && (
              <div className="absolute inset-0">
                <motion.div
                  className="absolute"
                  style={{ left: 70, top: 96, width: 820 }}
                  initial={{ opacity: 0, y: 30, rotate: -3 }}
                  animate={{ opacity: 1, y: [30, 0, -10], rotate: -3 }}
                  transition={{ duration: SEQ1_MS / 1000, ease: 'easeInOut', opacity: { duration: 0.6 } }}
                >
                  <BrowserChrome>
                    <ScaledFrame
                      src={HERO_SRC}
                      logicalW={1280}
                      logicalH={760}
                      targetW={820}
                      targetH={480}
                      cycle={cycle}
                    />
                  </BrowserChrome>
                </motion.div>

                <motion.div
                  className="absolute"
                  style={{ right: 90, top: 70 }}
                  initial={{ opacity: 0, y: 60, rotate: 4 }}
                  animate={{ opacity: 1, y: [60, 0, 8], rotate: 4 }}
                  transition={{ duration: SEQ1_MS / 1000, ease: 'easeInOut', delay: 0.25, opacity: { duration: 0.6, delay: 0.25 } }}
                >
                  <PhoneMock cycle={cycle} outerWidth={286} screenW={266} screenH={560} />
                </motion.div>
              </div>
            )}

            {/* ── MOBILE: single centered phone, portrait, slow zoom ── */}
            {mode === 'mobile' && (
              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div
                  initial={{ opacity: 0, scale: 0.99, y: 18 }}
                  animate={{ opacity: 1, scale: 1.05, y: 0 }}
                  transition={{
                    duration: SEQ1_MS / 1000,
                    ease: 'easeInOut',
                    opacity: { duration: 0.6 },
                  }}
                >
                  <PhoneMock cycle={cycle} outerWidth={560} screenW={530} screenH={1060} />
                </motion.div>
              </div>
            )}

            {/* Vignette for cinematic focus */}
            <div
              className="absolute inset-0 pointer-events-none z-20"
              style={{ boxShadow: 'inset 0 0 220px 60px rgba(0,0,0,0.55)' }}
            />

            <CaptionBar compact={mode === 'mobile'} />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* ───────────── Controls (outside the recordable stage) ───────────── */}
      <AnimatePresence>
        {showUI && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="fixed top-4 left-4 z-[130] flex flex-col gap-3 rounded-2xl border border-white/10 bg-black/70 backdrop-blur-md p-4 text-white shadow-2xl w-[268px]"
          >
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-sky-300/90">Break Room Cup · Promo</p>
              <p className="text-sm font-bold mt-0.5">SEQ 1 — Hook / Brand</p>
              <p className="text-[11px] text-white/55">0:00–0:08 · 8s · auto-loop</p>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setMode('web')}
                className={`inline-flex flex-col items-center justify-center gap-1 rounded-lg px-1 py-2 text-[11px] font-semibold transition-all ${
                  mode === 'web' ? 'bg-sky-500 text-white' : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
              >
                <Monitor size={15} /> Web
              </button>
              <button
                type="button"
                onClick={() => setMode('web-mobile')}
                className={`inline-flex flex-col items-center justify-center gap-1 rounded-lg px-1 py-2 text-[11px] font-semibold transition-all ${
                  mode === 'web-mobile' ? 'bg-sky-500 text-white' : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
              >
                <MonitorSmartphone size={15} /> Web+Mob
              </button>
              <button
                type="button"
                onClick={() => setMode('mobile')}
                className={`inline-flex flex-col items-center justify-center gap-1 rounded-lg px-1 py-2 text-[11px] font-semibold transition-all ${
                  mode === 'mobile' ? 'bg-sky-500 text-white' : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
              >
                <Smartphone size={15} /> Mobile
              </button>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={replay}
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-semibold bg-white/10 text-white hover:bg-white/20 transition-all"
              >
                <RotateCcw size={14} /> Replay
              </button>
              <button
                type="button"
                onClick={() => setLoop((l) => !l)}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-semibold transition-all ${
                  loop ? 'bg-emerald-500/90 text-white' : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
              >
                <Repeat size={14} /> Loop
              </button>
            </div>

            <button
              type="button"
              onClick={() => setShowUI(false)}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-semibold bg-white/10 text-white hover:bg-white/20 transition-all"
            >
              <EyeOff size={14} /> Hide UI for recording
            </button>

            <p className="text-[10px] leading-relaxed text-white/45">
              Record the framed area ({stage.w}×{stage.h}). Press <kbd className="px-1 rounded bg-white/15">Esc</kbd> to
              toggle this panel, <kbd className="px-1 rounded bg-white/15">R</kbd> to replay.
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {!showUI && (
        <button
          type="button"
          onClick={() => setShowUI(true)}
          className="fixed top-4 left-4 z-[130] w-9 h-9 rounded-full bg-black/40 hover:bg-black/70 border border-white/10 text-white/80 flex items-center justify-center transition-all"
          aria-label="Show controls"
        >
          <Eye size={16} />
        </button>
      )}

      {!loop && (
        <button
          type="button"
          onClick={replay}
          className="fixed bottom-4 right-4 z-[130] inline-flex items-center gap-2 rounded-full bg-sky-500 hover:bg-sky-400 text-white px-4 py-2.5 text-sm font-semibold shadow-xl transition-all"
        >
          <Play size={16} /> Play SEQ 1
        </button>
      )}
    </div>
  )
}

export default Promo
